import { useEffect, useState, type ClipboardEvent } from 'react'
import { CenteredFrame } from '../components/AppFrame'
import { CODE_LENGTH, RESEND_COOLDOWN_MS } from '../auth/pending-code'
import './SignInScreen.css'

/**
 * Provider slots.
 *
 * Microsoft OAuth is DESCOPED from Sept 15 and RESERVED here, not rendered.
 * A disabled or greyed Microsoft button generates support calls from reps who
 * try it, which is a worse outcome than its absence. Adding it later is one
 * entry in this array plus provider config in Supabase — the layout already
 * takes a third button without change.
 *
 *   { id: 'azure', label: 'Continue with Microsoft' },
 *
 * No provider logos: the Light Surface palette has no red and no orange, and
 * the Google mark carries both. Text-only keeps the palette rule intact.
 */
const PROVIDERS = [{ id: 'google', label: 'Continue with Google' }] as const

export type ProviderId = (typeof PROVIDERS)[number]['id']

/**
 * Why a code was refused. None of these depends on whether the address has
 * portal access: Supabase answers a wrong, expired or superseded code, and a
 * code for an address with no account, with the same 403. `expired` is
 * decided client-side from when this tab requested the code.
 */
export type CodeError = 'expired' | 'invalid' | 'rate_limited' | 'network'

const CODE_ERROR_TEXT: Record<CodeError, string> = {
  expired: 'That code has expired. Request a new one.',
  invalid: "That code didn't work.",
  rate_limited: 'Too many attempts. Wait a few minutes, then try again.',
  network: "We couldn't check that code. Check your connection and try again.",
}

/** Email-code sign-in, as AuthProvider exposes it. */
export interface EmailCode {
  /** Null means the email step. Set means a code was requested from this tab. */
  pending: { email: string; requestedAt: number } | null
  sending: boolean
  verifying: boolean
  error: CodeError | undefined
  requestCode: (email: string) => void
  verifyCode: (code: string) => void
  resendCode: () => void
  changeEmail: () => void
}

export function SignInScreen({
  onProvider,
  emailCode,
  providerError,
}: {
  onProvider: (id: ProviderId) => void
  emailCode: EmailCode
  providerError?: string | undefined
}) {
  return (
    <CenteredFrame>
      <h1>Rep Portal</h1>
      <p className="esl-muted esl-signin__intro">Sign in to see your orders and stock.</p>

      {/* OAuth first — a deliverability decision, not a design preference.
          Most reps then never touch email at all. */}
      <div className="esl-signin__providers">
        {PROVIDERS.map((p) => (
          <button
            key={p.id}
            type="button"
            className="esl-btn esl-btn--primary"
            onClick={() => onProvider(p.id)}
          >
            {p.label}
          </button>
        ))}
      </div>

      {providerError ? (
        <p className="esl-signin__notice" role="alert">
          {providerError}
        </p>
      ) : null}

      {/* Email is CO-PRIMARY, not a fallback. With Microsoft descoped it is
          the only door for M365 agencies, so it gets a real label, a real field
          and a full-width button — not a small link under the OAuth block. */}
      <div className="esl-signin__divider">
        <span>or sign in with your email</span>
      </div>

      {/* Both steps live on the SAME route. The address bar already carries
          the rep's destination (e.g. /sku/ABC123), so there is nothing to
          encode in a query string and nothing to lose on reload. */}
      {emailCode.pending ? (
        <CodeStep emailCode={emailCode} pending={emailCode.pending} />
      ) : (
        <EmailStep emailCode={emailCode} />
      )}

      <p className="esl-signin__foot">
        Access is by invitation. If you do not have access, contact ESL.
      </p>
    </CenteredFrame>
  )
}

function EmailStep({ emailCode }: { emailCode: EmailCode }) {
  const [email, setEmail] = useState('')
  const { sending } = emailCode

  return (
    <form
      className="esl-signin__form"
      onSubmit={(e) => {
        e.preventDefault()
        if (sending) return
        emailCode.requestCode(email)
      }}
    >
      <div className="esl-field">
        <label className="esl-label" htmlFor="esl-signin-email">
          Work email
        </label>
        <input
          id="esl-signin-email"
          className="esl-input"
          type="email"
          name="email"
          autoComplete="email"
          inputMode="email"
          autoCapitalize="none"
          spellCheck={false}
          required
          disabled={sending}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@youragency.com"
        />
      </div>

      {/* PRIMARY weight, not secondary. With Microsoft descoped, email is
          co-primary and is the ONLY door for M365 agencies — an outlined
          button beside a filled one reads as a fallback, which is exactly the
          hierarchy this screen must not imply. The divider above is what
          keeps two filled buttons from reading as a confusing pair. */}
      <button type="submit" className="esl-btn esl-btn--primary" disabled={sending}>
        {sending ? 'Sending…' : 'Email me a sign-in code'}
      </button>
    </form>
  )
}

function CodeStep({
  emailCode,
  pending,
}: {
  emailCode: EmailCode
  pending: { email: string; requestedAt: number }
}) {
  const { sending, verifying, error } = emailCode
  const [code, setCode] = useState('')
  /** The code the current `error` is about. The error is shown only while the
   *  field still holds that code, so it clears as soon as the rep edits. */
  const [submitted, setSubmitted] = useState<string | null>(null)
  const [now, setNow] = useState(() => Date.now())

  // A new request (resend) starts from an empty field.
  useEffect(() => {
    setCode('')
    setSubmitted(null)
    setNow(Date.now())
  }, [pending.requestedAt])

  const cooldownLeft = Math.max(0, pending.requestedAt + RESEND_COOLDOWN_MS - now)
  useEffect(() => {
    if (cooldownLeft <= 0) return
    const id = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [cooldownLeft > 0, pending.requestedAt])

  const submit = (digits: string) => {
    if (verifying || sending || digits.length !== CODE_LENGTH) return
    setSubmitted(digits)
    emailCode.verifyCode(digits)
  }

  // Digits only, whatever arrives: typed, autofilled from the Mail/Messages
  // suggestion, or pasted as "123 456". Reaching the full length submits, but
  // never the same code twice in a row by accident; the button still can.
  const accept = (raw: string) => {
    const digits = raw.replace(/\D/g, '').slice(0, CODE_LENGTH)
    setCode(digits)
    if (digits.length === CODE_LENGTH && digits !== submitted) submit(digits)
  }

  const onPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    // Handled here rather than left to the browser: maxLength would truncate
    // a pasted "123 456" to "123 45" before onChange ever saw it.
    e.preventDefault()
    accept(e.clipboardData.getData('text'))
  }

  const showError = error !== undefined && submitted !== null && code === submitted

  return (
    <>
      {/*
        THE UNIFORM MESSAGE.

        Identical for every address: sent, not allowlisted, inactive,
        throttled, provider error, network failure. `otp-request` returns the
        same 200 for all of those by design; this step must not undo that by
        rendering a different message, colour or delay for any of them. If you
        are about to add "no account found" here, don't.
      */}
      <div className="esl-signin__notice" role="status" aria-live="polite">
        <p>
          If <strong>{pending.email}</strong> has portal access, we've sent it a 6-digit code.
        </p>
        <p className="esl-signin__notice-sub">
          If no code arrives within 2 minutes, wait a few minutes before trying again.
        </p>
      </div>

      <form
        className="esl-signin__form esl-signin__form--code"
        onSubmit={(e) => {
          e.preventDefault()
          submit(code)
        }}
      >
        <div className="esl-field">
          <label className="esl-label" htmlFor="esl-signin-code">
            6-digit code
          </label>
          <input
            id="esl-signin-code"
            className="esl-input esl-signin__code"
            type="text"
            name="code"
            autoComplete="one-time-code"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={CODE_LENGTH}
            autoFocus
            disabled={verifying}
            value={code}
            onChange={(e) => accept(e.target.value)}
            onPaste={onPaste}
            aria-describedby={showError ? 'esl-signin-code-error' : undefined}
            aria-invalid={showError || undefined}
          />
        </div>

        {/* Weight and border, never hue: the palette has no red or orange. */}
        {showError ? (
          <p id="esl-signin-code-error" className="esl-signin__error" role="alert">
            {CODE_ERROR_TEXT[error]}
          </p>
        ) : null}

        <button
          type="submit"
          className="esl-btn esl-btn--primary"
          disabled={verifying || sending || code.length !== CODE_LENGTH}
        >
          {verifying ? 'Checking…' : 'Sign in'}
        </button>
      </form>

      <div className="esl-signin__code-actions">
        <button
          type="button"
          className="esl-btn esl-btn--quiet"
          disabled={sending || verifying || cooldownLeft > 0}
          onClick={emailCode.resendCode}
        >
          {sending
            ? 'Sending…'
            : cooldownLeft > 0
              ? `Send a new code (${Math.ceil(cooldownLeft / 1000)} s)`
              : 'Send a new code'}
        </button>
        <button
          type="button"
          className="esl-btn esl-btn--quiet"
          disabled={sending || verifying}
          onClick={emailCode.changeEmail}
        >
          Use a different email
        </button>
      </div>
    </>
  )
}
